import { FileDown } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, tokens } from '../lib/api';
import { Link } from 'react-router';
import type { Child, DocChild, DocFrom, Filing, StateDeadline } from '../lib/types';
import { Sheet } from '../ui/Sheet';
import { useParent } from './ParentContext';

/**
 * Deadlines and documents (#609, phase 4 of #453): this school year's filing deadlines for the family's state rule on
 * State compliance → Deadlines & documents (#954), marked filed as they're sent, and a notice, instruction plan or progress report filled in from
 * the family's records.
 */

const short = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const DOC_LABEL: Record<string, string> = { notice: 'Make notice', ihip: 'Make plan', report: 'Make report' };

interface DeadlineList {
  year: number;
  yearLabel: string;
  deadlines: StateDeadline[];
  reminderDays: number;
  documents: boolean;
}

export function Deadlines({ child }: { child: Child }) {
  const { toast } = useParent();
  const [data, setData] = useState<DeadlineList | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [doc, setDoc] = useState<StateDeadline | null>(null);
  const load = useCallback(() => {
    api
      .get<DeadlineList>(`/api/children/${child.id}/attendance/deadlines`)
      .then(setData)
      .catch(() => setData(null));
  }, [child.id]);
  useEffect(load, [load]);
  if (!data?.deadlines.length) return null;

  async function update(d: StateDeadline, patch: { filed?: boolean; date?: string }) {
    try {
      await api.put(`/api/children/${child.id}/attendance/deadlines/${d.id}/${d.year}`, patch);
      setMoving(null);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const soon = (d: StateDeadline) => !d.filedAt && d.daysLeft >= 0 && d.daysLeft <= (data.reminderDays || 14);

  return (
    <div className="deadlines stack">
      <div className="row-between wrap">
        <h3>Deadlines this school year</h3>
        {data.reminderDays > 0 && <span className="muted small">Reminders {data.reminderDays} days ahead, by push and email</span>}
      </div>
      {data.deadlines.map((d) => {
        const status = d.filedAt
          ? `✓ Filed ${short(d.filedAt)}`
          : d.daysLeft < 0
            ? `Was due ${short(d.date)} · not marked filed`
            : d.daysLeft === 0
              ? 'Due today'
              : d.daysLeft === 1
                ? 'Due tomorrow'
                : soon(d)
                  ? `Due in ${d.daysLeft} days`
                  : `In ${d.daysLeft} days`;
        const tone = d.filedAt ? 'good-text' : d.daysLeft < 0 ? 'bad-text' : soon(d) ? 'warn-text' : 'muted';
        const [mon, day] = short(d.date).split(' ');
        return (
          <div key={`${d.id}-${d.year}`} className={`deadline-row ${soon(d) ? 'soon' : ''}`}>
            <div className="deadline-date" aria-hidden="true">
              <span>{mon}</span>
              <strong>{day}</strong>
            </div>
            <div className="deadline-main">
              <strong>
                {d.title} {d.year !== data.year && <span className="tag small">{`${d.year}–${String(d.year + 1).slice(2)}`}</span>}
              </strong>
              <span className={`small ${tone}`}>
                {status}
                {d.ruleDate && !d.filedAt && <span className="muted"> · moved from {short(d.ruleDate)}</span>}
              </span>
              {moving === d.id && (
                <span className="row-gap wrap">
                  <input type="date" defaultValue={d.date} aria-label={`New date for ${d.title}`} onChange={(e) => e.target.value && update(d, { date: e.target.value })} />
                  {d.ruleDate && (
                    <button type="button" className="link-btn small" onClick={() => update(d, { date: '' })}>
                      Back to {short(d.ruleDate)}
                    </button>
                  )}
                </span>
              )}
            </div>
            <div className="deadline-actions">
              {d.familyDate && !d.filedAt && (
                <button type="button" className="link-btn small" onClick={() => setMoving(moving === d.id ? null : d.id)}>
                  {moving === d.id ? 'Done' : 'Change date'}
                </button>
              )}
              {d.doc && data.documents && (
                <button type="button" className="btn small" onClick={() => setDoc(d)}>
                  <FileDown size={15} /> {DOC_LABEL[d.doc]}
                </button>
              )}
              <button type="button" className={`btn small ${d.filedAt ? '' : 'primary'}`} onClick={() => update(d, { filed: !d.filedAt })}>
                {d.filedAt ? 'Undo' : 'Mark filed'}
              </button>
            </div>
          </div>
        );
      })}
      {data.deadlines.some((d) => d.familyDate) && <p className="muted small">Where your plan sets a date (like New York’s quarterly reports), use Change date.</p>}
      {doc && <DocSheet child={child} deadline={doc} onClose={() => setDoc(null)} />}
    </div>
  );
}

/**
 * Making a document (#954): nothing is typed in here. It shows what the PDF will be filled in from (the primary
 * parent's account, Where you file, and each child's profile) and links to what's missing; a child's legal name,
 * date of birth and grade must be there first. An IHIP's curriculum notes are the one thing edited here.
 */
function DocSheet({ child, deadline, onClose }: { child: Child; deadline: StateDeadline; onClose: () => void }) {
  const { toast, replaceChild } = useParent();
  const [data, setData] = useState<{ from: DocFrom; filing: Filing; children: DocChild[] } | null>(null);
  const [include, setInclude] = useState<Set<string>>(new Set());
  const [curriculum, setCurriculum] = useState('');
  const [busy, setBusy] = useState(false);
  // Keep a copy in Homeschool documents (#624).
  const [keep, setKeep] = useState(true);
  const notice = deadline.doc === 'notice';
  const kind = (deadline.doc ?? 'report') as 'notice' | 'ihip' | 'report';
  useEffect(() => {
    api
      .get<{ from: DocFrom; filing: Filing; children: DocChild[] }>(`/api/children/${child.id}/attendance/documents/summary`)
      .then((r) => {
        setData(r);
        setInclude(new Set(r.children.map((k) => k.id)));
        setCurriculum(r.children.find((k) => k.id === child.id)?.curriculum ?? '');
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [child.id, toast]);
  if (!data) return null;

  const shown = notice ? data.children : data.children.filter((k) => k.id === child.id);
  const picked = shown.filter((k) => !notice || include.has(k.id));
  const blocked = picked.filter((k) => k.missing[kind].length);
  const me = data.children.find((k) => k.id === child.id);
  const fromMissing = [!data.from.parentName && 'legal name', !data.from.address.trim() && 'home address'].filter(Boolean) as string[];
  const toMissing = !data.filing.district.trim();
  const list = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}` : (xs[0] ?? ''));

  async function make() {
    setBusy(true);
    try {
      if (deadline.doc === 'ihip' && me && curriculum !== me.curriculum) {
        replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { curriculum }));
      }
      const res = await fetch(`/api/children/${child.id}/attendance/documents/${deadline.id}/${deadline.year}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokens.parent()}` },
        body: JSON.stringify({ ...(notice ? { childIds: [...include] } : {}), save: keep }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiError(body?.error ?? `Couldn’t make it (${res.status}).`, res.status);
      }
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'document.pdf';
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      const saved = decodeURIComponent(res.headers.get('x-document-saved') ?? '');
      if (saved && saved !== 'ok') toast({ text: `Couldn’t keep a copy: ${saved}`, tone: 'alert' });
      toast({ text: `${deadline.title} is downloading${saved === 'ok' ? ', and a copy is in Homeschool documents' : ''}. Mark it filed once it’s sent.`, tone: 'good' });
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const ready = <span className="good-text small">Ready</span>;

  return (
    <Sheet title={`${deadline.title} · ${deadline.year}–${String(deadline.year + 1).slice(2)}`} onClose={onClose}>
      <div className="stack doc-sheet">
        <p className="muted small">
          Filled in from your account, Where you file and {notice ? 'each child’s profile' : `${child.name}’s profile`}. Nothing new is asked for here.
        </p>
        <h3 className="doc-head">From</h3>
        <div className="row-between wrap">
          <span>{[data.from.parentName, data.from.address.split('\n')[0]].filter(Boolean).join(' · ') || 'Your details for state paperwork'}</span>
          {fromMissing.length ? (
            <Link to="/settings#account" className="small" onClick={onClose}>
              Add your {list(fromMissing)}
            </Link>
          ) : (
            ready
          )}
        </div>
        <h3 className="doc-head">To</h3>
        <div className="row-between wrap">
          <span>
            {[data.filing.officialName && `${data.filing.officialName}, ${data.filing.officialTitle}`, data.filing.district].filter(Boolean).join(' · ') ||
              data.filing.officialTitle}
          </span>
          {toMissing ? (
            <Link to={`/kids/${child.id}/filing`} className="small" onClick={onClose}>
              Add your school district
            </Link>
          ) : (
            ready
          )}
        </div>
        <h3 className="doc-head">{notice ? 'Children' : child.name}</h3>
        {shown.map((k) => {
          const missing = k.missing[kind];
          const line = [
            k.legalName || k.name,
            kind !== 'report' && k.birthDate && `born ${new Date(`${k.birthDate}T12:00:00`).toLocaleDateString()}`,
            k.grade && (/^\d+(st|nd|rd|th)$/i.test(k.grade.trim()) ? `${k.grade.trim()} grade` : `grade ${k.grade}`),
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={k.id} className="row-between wrap doc-child">
              {notice && data.children.length > 1 ? (
                <label className="check compact">
                  <input
                    type="checkbox"
                    checked={include.has(k.id)}
                    onChange={(e) => {
                      const next = new Set(include);
                      if (e.target.checked) next.add(k.id);
                      else next.delete(k.id);
                      setInclude(next);
                    }}
                  />
                  <span>{line}</span>
                </label>
              ) : (
                <span>{line}</span>
              )}
              {missing.length ? (
                <Link to={`/kids/${k.id}/profile`} className="small" onClick={onClose}>
                  Add {list(missing)} on {k.name}’s profile
                </Link>
              ) : (
                ready
              )}
            </div>
          );
        })}
        {deadline.doc === 'ihip' && (
          <label>
            Curriculum and materials <span className="muted small">(one subject per line, like “Math: Saxon 3”)</span>
            <textarea
              rows={Math.max(3, me?.subjects.length ?? 3)}
              value={curriculum}
              onChange={(e) => setCurriculum(e.target.value)}
              maxLength={4000}
              placeholder={(me?.subjects.length ? me.subjects : ['Math', 'Reading']).map((s) => `${s}: `).join('\n')}
            />
          </label>
        )}
        <label className="check compact">
          <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />
          <span>Keep a copy in Homeschool documents (Settings → School Settings)</span>
        </label>
        <div className="sheet-actions doc-actions">
          <span className={`small ${blocked.length ? 'warn-text' : fromMissing.length || toMissing ? 'muted' : 'good-text'}`}>
            {blocked.length
              ? `Add ${blocked.map((k) => `${k.name}’s ${list(k.missing[kind])}`).join(', and ')} first.`
              : fromMissing.length || toMissing
                ? 'Anything still missing prints as a blank line to fill in by hand.'
                : 'Everything it needs is filled in.'}
          </span>
          <button className="btn primary" disabled={busy || !picked.length || blocked.length > 0} onClick={make}>
            <FileDown size={16} /> Make the PDF
          </button>
        </div>
      </div>
    </Sheet>
  );
}
